import { emailAddress } from 'utils'
import { getInitialAvatarSrc } from 'utils/ui'

import { getAvatarURL, getContactInfo } from './avatarService'
import dataManager from './DataManager'

export interface AddressInfo {
  name: string
  email: string
  avatarUrl: string
}

interface ContactInfo {
  id: string
  avatarUrl: string | null
  name: string | null
  email: string
}

interface EmailInfo {
  user: string
  host: string
}

type PromiseResolve = (value: AddressInfo) => void

export class AddressObj {
  email: string
  name: string
  host?: string
  fixedName: boolean
  loading: boolean
  loaded: boolean
  nameLoaded: boolean
  avatarLoaded: boolean
  info: AddressInfo
  contactInfo?: ContactInfo
  namePromiseList: PromiseResolve[]
  avatarPromiseList: PromiseResolve[]

  constructor(email: string, name?: string, nameFix: boolean = true) {
    this.email = email
    this.name = name || ''

    this.fixedName = false

    this.loading = false
    this.loaded = false
    this.nameLoaded = false
    this.avatarLoaded = false

    const emailInfo: EmailInfo | null = emailAddress(email)
    if (emailInfo != null) {
      this.host = emailInfo.host
    }

    if (this.name == null || this.name.length === 0) {
      this.name = emailInfo?.user || email
    } else if (nameFix === true) {
      this.fixedName = true
      this.nameLoaded = true
    }

    this.info = {
      name: this.name,
      email: this.email,
      avatarUrl: getInitialAvatarSrc(this.name, this.email),
    }

    this.namePromiseList = []
    this.avatarPromiseList = []
  }

  private load = (): void => {
    const self = this

    if (self.loading === false && self.loaded === false) {
      self.loading = true

      const callGetAvatar = () => {
        getAvatarURL(self.info.name, self.email).then((url: string | null) => {
          if (url != null) {
            self.loading = false
            self.loaded = true

            self.info.avatarUrl = url
            self.avatarLoaded = true

            self.avatarPromiseList.forEach((resolve) => {
              resolve(self.info)
            })
            self.avatarPromiseList = []
          }
        })
      }

      if (self.fixedName === false) {
        getContactInfo(self.email).then((info: ContactInfo | null) => {
          if (info != null) {
            self.info.name = info.name ?? self.info.name
            self.contactInfo = info

            const defaultSrc = getInitialAvatarSrc(self.info.name, self.email)
            self.info.avatarUrl = defaultSrc

            self.nameLoaded = true

            self.namePromiseList.forEach((resolve) => {
              resolve(self.info)
            })
            self.namePromiseList = []

            callGetAvatar()
          } else {
            dataManager.getTempEmailList().then(({ dataList }: { dataList: TempEmailItem[] }) => {
              const findEmailInfo = (email: string) => {
                const item = dataList.find((item: TempEmailItem) => {
                  return item.data.email === email
                })

                if (item == null) {
                  return { email, name: email }
                }
                return item.data
              }

              const emailInfo = findEmailInfo(self.email)
              if (emailInfo != null) {
                self.info.name = emailInfo.name
                const defaultSrc = getInitialAvatarSrc(self.info.name, self.email)
                self.info.avatarUrl = defaultSrc
              }

              self.nameLoaded = true

              self.namePromiseList.forEach((resolve) => {
                resolve(self.info)
              })
              self.namePromiseList = []

              callGetAvatar()
            })
          }
        })
      } else {
        callGetAvatar()
      }
    }
  }

  getInfoForAvatar = (): Promise<AddressInfo> => {
    if (this.avatarLoaded === false) {
      return new Promise((resolve) => {
        this.avatarPromiseList.push(resolve)
        this.load()
      })
    } else {
      return Promise.resolve(this.info)
    }
  }

  getInfoForName = (): Promise<AddressInfo> => {
    if (this.nameLoaded === false) {
      return new Promise((resolve) => {
        this.namePromiseList.push(resolve)
        this.load()
      })
    } else {
      return Promise.resolve(this.info)
    }
  }

  getInfo = (): AddressInfo => {
    return this.info
  }
}

interface TempEmailData {
  email: string
  name: string
}

interface TempEmailItem {
  data: TempEmailData
}

export default AddressObj
